import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { LoginForm, type LoginFormProps } from "./LoginForm";
import { AuthHttpError, type PasswordCredentials } from "../types";

export interface LoginPageProps extends Pick<LoginFormProps, "identifierLabel" | "showRememberMe" | "className"> {
  title?: string;
  subtitle?: string;
  /** Called after a successful login — typically a router navigation to the app's home. */
  onSuccess?: () => void;
  containerClassName?: string;
}

function friendlyMessage(err: unknown): string {
  if (err instanceof AuthHttpError) {
    if (err.status === 401 || err.status === 400) return "Incorrect email/username or password.";
    if (err.status === 429) return "Too many attempts. Try again shortly.";
    if (err.code === "NETWORK_ERROR") return "Can't reach the server. Check your connection.";
    return err.message || "Sign in failed. Please try again.";
  }
  return "Sign in failed. Please try again.";
}

/** Ready-to-route login page. Swap it entirely with your own — it's just a composition of `useAuth()` + `LoginForm`. */
export function LoginPage({
  title = "Sign in",
  subtitle,
  onSuccess,
  containerClassName,
  ...formProps
}: LoginPageProps) {
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (credentials: PasswordCredentials) => {
    setLoading(true);
    setError(null);
    try {
      await auth.login(credentials);
      onSuccess?.();
    } catch (err) {
      setError(friendlyMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={
        containerClassName ??
        "fka-login-page flex min-h-screen items-center justify-center bg-slate-950 px-4"
      }
    >
      <div className="fka-login-card w-full max-w-sm rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h1 className="fka-login-title mb-1 text-xl font-semibold text-slate-100">{title}</h1>
        {subtitle && <p className="fka-login-subtitle mb-6 text-sm text-slate-400">{subtitle}</p>}
        <LoginForm onSubmit={handleSubmit} loading={loading} error={error} {...formProps} />
      </div>
    </div>
  );
}
